// Stillhouse view — Machines & pools. Machines tab is LIVE (from /api/fleet).
// "Provision machine" opens the host-enrollment modal.
import React, { useCallback, useEffect, useState } from "react";
import { C, F, Btn } from "../brand/apo";
import { Card, StatusDot, Avatar, Tabs, PageHeader, Stat, EmptyState } from "../brand/sh";
import { Modal } from "../components/Modal";
import { EnrollHostForm } from "../components/EnrollHostForm";
import { GenerateHostInstall } from "../components/GenerateHostInstall";
import { api, STATUS_KEY, type FleetMachine, type MachineStatus } from "../api";

const Scroll = ({ children }: { children: React.ReactNode }) => (
  <div style={{ flex: 1, overflow: "auto", padding: "22px 32px", display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
);

export function MachinesPools() {
  const [tab, setTab] = useState("machines");
  const [machines, setMachines] = useState<FleetMachine[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState("agent");

  const load = useCallback(async () => {
    try {
      const r = await api.getMachines();
      setMachines(r.machines);
      setLoadError(false);
    } catch {
      setMachines([]);
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const list = machines ?? [];
  const count = (s: MachineStatus) => list.filter((m) => m.status === s).length;
  const online = count("ONLINE");
  const inUse = count("IN_USE");
  const offline = count("OFFLINE");
  const needsAction = count("ERROR") + count("MAINTENANCE");

  return (
    <React.Fragment>
      <PageHeader
        crumb="FLEET › MACHINES"
        title="Machines & pools"
        sub="Every MoonShine host the stillhouse controls. Assign machines to people or teams, group them into shared pools, and provision new rigs remotely."
      >
        <Btn ghost>Group into pool</Btn>
        <Btn primary onClick={() => setModalOpen(true)}>
          Provision machine
        </Btn>
      </PageHeader>
      <div style={{ padding: "0 32px", borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { id: "machines", label: "All machines", count: list.length },
            { id: "pools", label: "Shared pools", count: 0 },
          ]}
        />
      </div>
      <Scroll>
        {tab === "machines" && (
          <React.Fragment>
            {loadError && (
              <div style={{ fontFamily: F.mono, fontSize: 11, color: C.rose, letterSpacing: "0.04em" }}>
                Could not reach the fleet API.
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
              <Stat label="Online" value={String(online)} accent={C.glow} />
              <Stat label="In use" value={String(inUse)} accent={C.lilac} />
              <Stat label="Offline" value={String(offline)} accent={C.fgFaint} />
              <Stat label="Needs action" value={String(needsAction)} sub="error · maintenance" accent={C.rose} />
            </div>
            <Card pad={false}>
              <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.2fr 1fr 1fr 1.2fr 0.7fr", gap: 14, padding: "11px 18px", fontFamily: F.mono, fontSize: 10, letterSpacing: "0.16em", color: C.fgFaint, fontWeight: 600, borderBottom: `1px solid ${C.line}`, background: C.inkHi }}>
                <span>MACHINE</span>
                <span>SPEC</span>
                <span>LOCATION</span>
                <span>STATUS</span>
                <span>ASSIGNED TO</span>
                <span></span>
              </div>
              {list.length === 0 && (
                <div style={{ padding: "28px 18px", color: C.fgMute, fontSize: 13 }}>
                  No machines yet. Click <span style={{ color: C.glow }}>Provision machine</span> to enroll a host.
                </div>
              )}
              {list.map((m, i) => (
                <div key={m.id} style={{ display: "grid", gridTemplateColumns: "1.2fr 1.2fr 1fr 1fr 1.2fr 0.7fr", gap: 14, padding: "13px 18px", borderBottom: i < list.length - 1 ? `1px solid ${C.line}` : "none", alignItems: "center", fontFamily: F.ui, fontSize: 13 }}>
                  <span style={{ fontFamily: F.mono, fontWeight: 700, letterSpacing: "0.03em", color: C.fgDark }}>{m.hostname}</span>
                  <span style={{ fontFamily: F.mono, fontSize: 11.5, color: C.fgMute }}>{m.spec ?? "—"}</span>
                  <span style={{ color: C.fgMute }}>{m.location ?? "—"}</span>
                  <StatusDot status={STATUS_KEY[m.status]} />
                  <span style={{ display: "flex", alignItems: "center", gap: 8, color: C.fgDark }}>
                    {m.assignedTo ? (
                      <React.Fragment>
                        <Avatar name={m.assignedTo} size={24} />
                        {m.assignedTo}
                      </React.Fragment>
                    ) : (
                      <span style={{ fontFamily: F.mono, fontSize: 11, color: C.fgFaint }}>{m.team ?? "unassigned"}</span>
                    )}
                  </span>
                  <span style={{ textAlign: "right" }}>
                    <Btn ghost size="md" style={{ padding: "5px 12px", fontSize: 12 }}>
                      Manage
                    </Btn>
                  </span>
                </div>
              ))}
            </Card>
          </React.Fragment>
        )}
        {tab === "pools" && (
          <Card pad={false}>
            <EmptyState title="No shared pools yet" sub="GROUP MACHINES INTO A POOL TO GET STARTED" />
          </Card>
        )}
      </Scroll>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Provision machine"
        sub="Add a MoonShine host. Generate an install config the host enrolls itself with, or add an already-running host by address."
        width={560}
      >
        <div style={{ marginBottom: 18 }}>
          <Tabs
            value={modalTab}
            onChange={setModalTab}
            tabs={[
              { id: "agent", label: "Install via agent" },
              { id: "existing", label: "Add existing host" },
            ]}
          />
        </div>
        {modalTab === "agent" ? (
          <GenerateHostInstall />
        ) : (
          <EnrollHostForm
            onEnrolled={() => {
              setModalOpen(false);
              void load();
            }}
            onCancel={() => setModalOpen(false)}
          />
        )}
      </Modal>
    </React.Fragment>
  );
}
